import {
  ClockCircleFilled,
  DownOutlined,
  ExclamationCircleOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { Alert, Col, Divider, Modal, Row, Typography } from "antd";
import React, { useEffect, useState } from "react";
import ImageSuccess from "../../../asset/modal/successModal.gif";
import { PATCH2 } from "../../../services";
import { RE_INSERT_ORDER } from "../../../services/path";
import CountdownTimer from "./countdownTimer";
import SuccessMessage from "./successMessage";

interface UploadAlertProps {
  open: boolean;
  onCancel: () => void;
  order_no: string[];
  diff_order_no: string[];
  platfrom: string;
  brand: string;
  platfrom_id: number;
  shop_id: string;
  uploadTime: string;
}

const { confirm } = Modal;
const UploadAlert: React.FC<UploadAlertProps> = ({
  open,
  onCancel,
  order_no,
  diff_order_no,
  platfrom,
  brand,
  platfrom_id,
  shop_id,
  uploadTime,
}) => {
  const initialTime = diff_order_no.length * 4;
  const [remainingTime, setRemainingTime] = useState(initialTime);
  const [timeUsed, setTimeUsed] = useState(0);
  const [isCountdownFinished, setIsCountdownFinished] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isImageSuccess, setImageSuccess] = useState<string | null>(null);
  const handleReInsertOrder = () => {
    const payload = {
      ListOrder: diff_order_no,
      BrandName: brand,
      PlatfID: platfrom_id,
      ShopID: shop_id,
    };
    PATCH2(RE_INSERT_ORDER, payload);
  };

  useEffect(() => {
    if (open) {
      setIsCountdownFinished(false);
      setIsTransitioning(false);
      setRemainingTime(initialTime);
      handleReInsertOrder();
    }
  }, [open, initialTime]);

  useEffect(() => {
    const img = new Image();
    img.src = ImageSuccess;
    img.onload = () => {
      setImageSuccess(ImageSuccess);
    };
  }, []);

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (open) {
      timerId = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime > 0) {
            setTimeUsed(initialTime - prevTime + 1);
            return prevTime - 1;
          } else {
            clearInterval(timerId);
            setIsTransitioning(true);
            setTimeout(() => {
              setIsCountdownFinished(true);
              setTimeUsed(initialTime);
              setIsTransitioning(false);
            }, 100);

            return 0;
          }
        });
      }, 1000);
    }
    return () => clearInterval(timerId);
  }, [open, initialTime]);

  const formatTime = (totalTimeInSeconds: number) => {
    const days = Math.floor(totalTimeInSeconds / (3600 * 24));
    const hours = Math.floor((totalTimeInSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalTimeInSeconds % 3600) / 60);
    const seconds = totalTimeInSeconds % 60;
    return { days, hours, minutes, seconds };
  };

  const {
    days: usedDays,
    hours: usedHours,
    minutes: usedMinutes,
    seconds: usedSeconds,
  } = formatTime(timeUsed);
  const displayTimeUsed = () => {
    const timeUnits = [
      usedDays > 0 ? `${usedDays} days` : "",
      usedHours > 0 ? `${usedHours} hours` : "",
      usedMinutes > 0 ? `${usedMinutes} minutes` : "",
      usedSeconds > 0 ? `${usedSeconds} seconds` : "",
    ];

    return timeUnits.filter((unit) => unit !== "").join(" ");
  };

  const handleCancel = () => {
    confirm({
      title: "ต้องการที่จะปืดใช่ไหม?",
      icon: <ExclamationCircleOutlined style={{ fontSize: "40px", color: "#faad14" }} />, // Increase icon size
      content: (
        <div style={{ paddingLeft: "18px" }}>ถ้าปิดระบบจะยังทำงานอยู่ตามเวลาที่ประมาณค่าไว้.</div>
      ),
      okText: "Yes",
      cancelText: "No",
      onOk() {
        onCancel();
      },
    });
  };

  return (
    <Modal open={open} onCancel={handleCancel} footer={null} width={700} centered>
      <div className={`transition-container ${isTransitioning ? "transitioning" : ""}`}>
        {isCountdownFinished ? (
          <SuccessMessage
            platfrom={platfrom}
            brand={brand}
            totalOrder={order_no.length}
            orderNoLength={diff_order_no.length}
            image={isImageSuccess ?? "Unknown image"}
            timeUsed={displayTimeUsed()}
            uploadTime={uploadTime}
          />
        ) : (
          <>
            <div style={{ textAlign: "center", padding: "0px 20px 20px 20px" }}>
              {/* <ExclamationCircleOutlined style={{ fontSize: '50px', color: '#ffa500' }} /> */}
              <Row justify="center" align="middle" style={{ marginBottom: "20px" }}>
                <Col>
                  <ClockCircleFilled
                    style={{
                      fontSize: "30px",
                      color: "#faad14",
                      marginRight: "8px",
                      marginTop: "16px",
                    }}
                  />
                </Col>
                <Col>
                  <Typography.Title level={2} type="secondary">
                    เวลาที่ใช้สำหรับ Recheck Order...
                  </Typography.Title>
                </Col>
              </Row>
              <CountdownTimer remainingTime={remainingTime} />
              <Row justify="center" align="middle" style={{ marginTop: "40px" }}>
                <Col span={12}>
                  <Typography.Text strong style={{ fontSize: "22px", color: "#656565" }}>
                    Platform: {platfrom}
                  </Typography.Text>
                </Col>
                <Col span={12}>
                  <Typography.Text strong style={{ fontSize: "22px", color: "#656565" }}>
                    Brand: {brand}
                  </Typography.Text>
                </Col>
              </Row>
              <Row gutter={[16, 16]} justify="center" style={{ marginTop: "30px" }}>
                <Col span={12} style={{ display: "flex", justifyContent: "center" }}>
                  <div style={highlightBoxStyle("#ffa500")}>
                    <Typography.Text strong style={highlightTextStyle}>
                      จำนวนที่ขาด: {diff_order_no.length}
                    </Typography.Text>
                  </div>
                </Col>
                <Col span={12} style={{ display: "flex", justifyContent: "center" }}>
                  <div style={highlightBoxStyle("#007bff")}>
                    <Typography.Text strong style={highlightTextStyle}>
                      จำนวนทั้งหมด: {order_no.length}
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
              <div
                onClick={() => setShowDetails(!showDetails)}
                style={{ cursor: "pointer", marginTop: "20px" }}
              >
                {showDetails ? (
                  <UpOutlined style={{ fontSize: "10px", color: "#656565" }} />
                ) : (
                  <DownOutlined style={{ fontSize: "10px", color: "#656565" }} />
                )}
              </div>
              {showDetails && (
                <>
                  <Divider />
                  <Row
                    gutter={[16, 16]}
                    style={{ maxWidth: "700px", width: "100%", textAlign: "center" }}
                  >
                    <Col span={12}>
                      <Typography.Text
                        strong
                        style={{
                          width: "100px",
                          display: "inline-block",
                          textAlign: "right",
                          paddingRight: "10px",
                        }}
                      >
                        ร้าน:
                      </Typography.Text>{" "}
                      {brand}
                    </Col>
                    <Col span={12}>
                      <Typography.Text
                        strong
                        style={{
                          width: "150px",
                          display: "inline-block",
                          textAlign: "right",
                          paddingRight: "10px",
                        }}
                      >
                        จำนวนจาก Excel:
                      </Typography.Text>{" "}
                      {order_no.length}
                    </Col>
                    <Col span={12}>
                      <Typography.Text
                        strong
                        style={{
                          width: "100px",
                          display: "inline-block",
                          textAlign: "right",
                          paddingRight: "10px",
                        }}
                      >
                        แพลตฟอร์ม:
                      </Typography.Text>{" "}
                      {platfrom}
                    </Col>
                    <Col span={12}>
                      <Typography.Text
                        strong
                        style={{
                          width: "150px",
                          display: "inline-block",
                          textAlign: "right",
                          paddingRight: "10px",
                        }}
                      >
                        จำนวนที่ Re-check:
                      </Typography.Text>{" "}
                      {diff_order_no.length}
                    </Col>
                    <Col span={12}>
                      <Typography.Text
                        strong
                        style={{
                          width: "100px",
                          display: "inline-block",
                          textAlign: "right",
                          paddingRight: "10px",
                        }}
                      >
                        Shop ID:
                      </Typography.Text>{" "}
                      {shop_id}
                    </Col>
                    <Col span={12}>
                      <Typography.Text
                        strong
                        style={{
                          width: "150px",
                          display: "inline-block",
                          textAlign: "right",
                          paddingRight: "10px",
                        }}
                      >
                        ผลต่าง:
                      </Typography.Text>
                      <Typography.Text strong style={{ color: "red" }}>
                        {order_no.length - diff_order_no.length}
                      </Typography.Text>
                    </Col>
                  </Row>
                  <Divider />
                </>
              )}
            </div>
            <Alert
              message="คำเตือน"
              description={
                <>
                  <Col>
                    <Typography.Text strong style={{ color: "red" }}>
                      *** เวลานี้เป็นแค่เวลาประมาณเท่านั้น
                    </Typography.Text>{" "}
                    หากเวลานานเกินกว่าที่คาดไว้
                    โปรดตรวจสอบให้แน่ใจว่าเวลาการประมวลผลเป็นที่ยอมรับได้สำหรับความต้องการดำเนินงานของคุณ.
                  </Col>
                  <Col style={{ marginTop: "10px" }}>
                    <Typography.Text strong style={{ color: "black" }}>
                      * Uploaded at: {uploadTime}
                    </Typography.Text>
                  </Col>
                </>
              }
              type="warning"
              showIcon
            />
          </>
        )}
      </div>
      <style>{`
          .transition-container {
            transition: opacity 0.3s ease;
          }
          .transitioning {
            opacity: 0;
          }
        `}</style>
    </Modal>
  );
};

const highlightBoxStyle = (backgroundColor: string): React.CSSProperties => ({
  backgroundColor,
  padding: "10px",
  borderRadius: "8px",
  textAlign: "center",
  width: "100%", // Adjust width as needed
  maxWidth: "200px", // Ensure boxes don't stretch too wide
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Shadow effect
});

const highlightTextStyle: React.CSSProperties = {
  fontSize: "18px",
  color: "#fff",
};

export default UploadAlert;
